import { inject } from '@angular/core';
import { selectHasAnyAccountWithMfaEnabled, selectSelectedAccount } from '@mkp/account/state';
import { applicationPageActions } from '@mkp/application/state/actions';
import { isApplicationUrl } from '@mkp/application/util';
import { authActions } from '@mkp/auth/actions';
import { SentryService } from '@mkp/core/util-sentry';
import { ClaimCompanyActions } from '@mkp/onboarding/feature-claim-company/actions';
import { TealiumService } from '@mkp/tracking/feature-tealium';
import { TrackingEvent } from '@mkp/tracking/models';
import { TrackingActions } from '@mkp/tracking/state/actions';
import { getGlobalCategory, TrackingService } from '@mkp/tracking/util-tracking';
import { transientAppCacheActions } from '@mkp/transient-app-cache/actions';
import {
  selectCompanyWasClaimed,
  selectLoginMethod,
  selectLoginStarted,
} from '@mkp/transient-app-cache/state';
import { loginActions } from '@mkp/user/feature-login/actions';
import { passwordlessLoginActions } from '@mkp/user/feature-passwordless-login/actions';
import { selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import {
  createVacancyWrapperActions,
  listVacancyActions,
  VacancyEditActions,
} from '@mkp/vacancy/state/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { combineLatest, exhaustMap, filter, map, Observable, switchMap, tap } from 'rxjs';

export const loadTealiumStartEvent$ = createEffect(
  (actions$ = inject(Actions), tealiumService = inject(TealiumService)) =>
    actions$.pipe(
      ofType(TrackingActions.loadTealiumStart),
      exhaustMap(() =>
        tealiumService.isBooted$.pipe(
          map((isBooted) =>
            isBooted ? TrackingActions.loadTealiumSuccess() : TrackingActions.loadTealiumFailed()
          )
        )
      )
    ),
  { functional: true, useEffectsErrorHandler: false }
);

export const loadTealiumStartFailed$ = createEffect(
  (actions$ = inject(Actions), sentryService = inject(SentryService)) =>
    actions$.pipe(
      ofType(TrackingActions.loadTealiumFailed),
      tap(() =>
        sentryService.captureException(
          new Error(`TealiumService Not Found - utag was not found (loadTealiumStartFailed$)`)
        )
      )
    ),
  { functional: true, dispatch: false, useEffectsErrorHandler: false }
);

// CDH-1819 - click events need to send the global-category, so it is synced in the tealium-service
export const syncTealiumGlobalCategory$ = createEffect(
  (store = inject(Store), tealiumService = inject(TealiumService)) =>
    combineLatest([store.select(selectLoggedInUser), store.select(selectSelectedAccount)]).pipe(
      map(([user, account]) => getGlobalCategory(user, account)),
      tap((globalCategory) => tealiumService.setGlobalCategory(globalCategory))
    ),
  { functional: true, dispatch: false }
);

/**
 * @description detect browser navigation and emit a page_view event
 */

export const sendPageViewEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    tealiumService = inject(TealiumService),
    trackingService = inject(TrackingService)
  ) =>
    actions$.pipe(
      ofType(routerNavigatedAction),
      filter(({ payload }) => !isApplicationUrl(payload.routerState.url)),
      sendPageViewEvent(tealiumService, trackingService)
    ),
  { functional: true, dispatch: false }
);

// CDH-988 - avoid double page-view events on /applications by triggering them manually

export const sendApplicationsPageViewEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    tealiumService = inject(TealiumService),
    trackingService = inject(TrackingService)
  ) =>
    actions$.pipe(
      ofType(applicationPageActions.queryParamsUpdated),
      sendPageViewEvent(tealiumService, trackingService)
    ),
  { functional: true, dispatch: false }
);

/**
 * @description main effect to dispatch a Pure Frontend Event
 */

export const sendPureFrontendEvents$ = createEffect(
  (actions$ = inject(Actions), tealiumService = inject(TealiumService)) =>
    actions$.pipe(
      ofType(TrackingActions.sendPureFrontendEvent),
      map(removeTypeProperty),
      map((category) => tealiumService.link(category))
    ),
  { functional: true, dispatch: false }
);

export const sendLoginStartEvents$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(
        loginActions.usernamePasswordSubmittedSuccess,
        passwordlessLoginActions.linkRequestedSuccess
      ),
      map(({ authMethod }) =>
        trackingService.getLoginEvent({
          user: undefined,
          account: undefined,
          authMethod,
          eventName: 'user_login_start',
        })
      ),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

// we send the login/signup event after loading initial account, because this call can fail
// -> if the call fails: user is forced logged-out, so we shouldn't send login event
//
// Previously, both regular and BO user loaded initial account
// -> this means that historically we have been sending login event for BO user
// -> we could check with marketing if they still need this (probably not)
//
// Now BO user no longer load an initial account (because it's useless for them)
// -> to avoid regressions I manually added the login/signup event for BO user
// -> this is done by determining when we consider a BO user to be logged-in.
// -> since they don't load initial account they are logged-in slightly before regular user
// -> they are logged-in when they receive the membership count
// -> cf code in hasInitialAccountOrIsBoUser
// TODO: check with marketing if we still need to send login event for BO user manually
export const sendLoginEvent$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(authActions.loadAccountsAfterLoginSuccess),
      filter(isLoadInitialAccountSuccessAction),
      filterOnlyIfLoginHasStarted(store),
      concatLatestFrom(() => [
        store.select(selectLoginMethod),
        store.select(selectLoggedInUser),
        store.select(selectSelectedAccount),
        store.select(selectHasAnyAccountWithMfaEnabled),
      ]),
      map(([, loginMethod, user, account, isMfaEnabled]) =>
        trackingService.getLoginEvent({
          user,
          account,
          authMethod: loginMethod,
          eventName: 'user_login',
          eventDetails: isMfaEnabled ? 'mfa enabled' : 'mfa disabled',
        })
      ),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

export const sendSignupEvent$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(authActions.loadAccountsAfterLoginSuccess),
      filter(isLoadInitialAccountSuccessAction),
      filterOnlyIfCompanyWasClaimed(store),
      concatLatestFrom(() => [
        store.select(selectLoggedInUser),
        store.select(selectSelectedAccount),
      ]),
      map(([, user, account]) => trackingService.getSignupEvent({ user, account })),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description create user event
 * This effect detects when a user is created and dispatches the respective event.
 */

export const userRegistrationEvent$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(TrackingActions.sendRegistrationEvent),
      map((user) => trackingService.getUserRegistrationEvent(user)),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description job ad creation start (SE-647)
 * This effect detects when a user starts a new job-ad, and dispatches that event
 */

export const jobAdCreateStartEvent$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(TrackingActions.sendJobAdCreateStartEvent),
      map(() => trackingService.getJobAdCreateStartEvent()),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description the active step in the stepper is changed during job ad creation
 * This effect detects when the user clicks on the stepper or when the step changes automatically
 */

export const activeStepChanged$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(createVacancyWrapperActions.activeStepChanged),
      map(({ stepIndex, nbSuggestions, vacancyTemporaryId, vacancyId }) =>
        trackingService.getJobAdCreateStepEvent({
          stepIndex,
          nbSuggestions,
          vacancyTemporaryId,
          vacancyId,
        })
      ),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description go back button was clicked in the job-ad creation funnel
 * This effect detects when a user clicks on the go back button, while at any step in the vacancy creation
 */

export const goBackButtonClicked$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(createVacancyWrapperActions.goBackButtonClicked),
      map(({ stepIndex }) => trackingService.getJobAdBackButtonEvent(stepIndex)),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description "suggest a job ad" button was clicked in the job-ad creation funnel
 * This effect detects when a user clicks on the "suggest a job ad" button, in the first step of the vacancy creation process
 */

export const suggestButtonClicked$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(VacancyEditActions.suggestButtonClicked),
      map(({ vacancyId: job_id, vacancyTemporaryId: job_tmp_id }) =>
        trackingService.getJobAdSuggestButtonEvent({ job_id, job_tmp_id })
      ),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description "stop generate" button was clicked in the job-ad creation funnel
 * This effect detects when a user clicks on the "stop generate" button, in the first step of the vacancy creation process
 */

export const cancelSuggestButtonClicked$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(VacancyEditActions.cancelSuggestButtonClicked),
      map(({ vacancyId: job_id, vacancyTemporaryId: job_tmp_id }) =>
        trackingService.getJobAdCancelSuggestButtonEvent({ job_id, job_tmp_id })
      ),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description the active step in the stepper is changed during job ad creation
 * This effect detects when the user clicks on the stepper or when the step changes automatically
 */

export const deleteDraftVacancy$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(listVacancyActions.deleteDraftVacancy),
      map(({ id }) => trackingService.getJobAdDeleteDraftEvent(id)),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

export const contactCustomerSupport$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(ClaimCompanyActions.contactCustomerSupport),
      map(() => trackingService.getContactCustomerSupportEvent()),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

/**
 * @description logo guidelines
 * This effect detects when a user clicks on a button in order to open logo guidelines
 */

export const getLogoGuidelinesEvent$ = createEffect(
  (actions$ = inject(Actions), trackingService = inject(TrackingService)) =>
    actions$.pipe(
      ofType(TrackingActions.sendOpenLogoGuidelinesEvent),
      map(() => trackingService.getLogoGuidelinesEvent()),
      map(TrackingActions.sendPureFrontendEvent)
    ),
  { functional: true }
);

const removeTypeProperty = (
  categoryFull: ReturnType<typeof TrackingActions.sendPureFrontendEvent>
): TrackingEvent => {
  const { type: _, ...category } = { ...categoryFull };
  return category;
};

type LoadInitialAccountAction = ReturnType<typeof authActions.loadAccountsAfterLoginSuccess>;
type LoginAction = LoadInitialAccountAction;
const isLoadInitialAccountSuccessAction = (
  loginAction: LoginAction
): loginAction is LoadInitialAccountAction =>
  (loginAction as LoadInitialAccountAction)?.accounts?.length > 0;

const filterOnlyIfLoginHasStarted =
  (store: Store) =>
  <T>(source: Observable<T>): Observable<T> =>
    source.pipe(
      switchMap((value) =>
        store.select(selectLoginStarted).pipe(
          filter(Boolean),
          tap(() => {
            store.dispatch(transientAppCacheActions.loginStartedUsed());
          }),
          map(() => value)
        )
      )
    );

const filterOnlyIfCompanyWasClaimed =
  (store: Store) =>
  <T>(source: Observable<T>): Observable<T> =>
    source.pipe(
      switchMap((value) =>
        store.select(selectCompanyWasClaimed).pipe(
          filter(Boolean),
          tap(() => {
            store.dispatch(transientAppCacheActions.companyWasClaimedUsed());
          }),
          map(() => value)
        )
      )
    );

const sendPageViewEvent =
  (tealiumService: TealiumService, trackingService: TrackingService) =>
  <T>(source: Observable<T>) =>
    source.pipe(
      map(() => trackingService.getPageViewEvent()),
      exhaustMap(tealiumService.view)
    );
